import { Factory } from "@/data/dao/Factory";
import { type IOfflineStorage, OfflineStorage } from "@/data/dao/offline/OfflineStorage";
import { ProfileDao } from "@/data/dao/service/ProfileDao";
import type { ICreditCard } from "@/data/model/CreditCard";
import { Signal } from "@/data/util/Signal";

import { logError } from "@/shared/logging/errors";

import { TastierClient } from "@/app/TastierClient";
import type { Identity } from "@/app/context/Identity";
import { PaymentMethodSelector } from "@/app/elements/PaymentMethodSelector";

export class UserCreditCardsService {
    profileDao: ProfileDao;
    offlineCache: IOfflineStorage;
    identityService: Identity;

    // events
    static onCreditCardsChanged = new Signal();

    constructor() {
        this.profileDao = Factory.getInstance<ProfileDao>(ProfileDao);
        this.offlineCache = Factory.getInstance<IOfflineStorage>(OfflineStorage);
        this.identityService = TastierClient.identityContext;
    }

    public addCreditCardLocal(card: ICreditCard) {
        this.offlineCache.write(PaymentMethodSelector.pmtMethodCacheKey, card.id);
        this.offlineCache.write(PaymentMethodSelector.pmtMethodTypeCacheKey, "card");

        const profile = this.identityService.getProfile()!;
        profile.creditCards = [card, ...profile.creditCards];
        UserCreditCardsService.onCreditCardsChanged.dispatch();
    }

    public removeCreditCardLocal(id: string) {
        const profile = this.identityService.getProfile()!;

        const existing = profile.creditCards.find((c) => c.id === id);
        if (existing !== undefined) {
            profile.creditCards.splice(profile.creditCards.indexOf(existing), 1);
        }

        UserCreditCardsService.onCreditCardsChanged.dispatch();
    }

    public async removeCreditCard(id: string): Promise<void> {
        try {
            await this.profileDao.removeCreditCard(id, {
                socketConnectionId: TastierClient.context.socket.connectionId!,
            });
            this.removeCreditCardLocal(id);
        } catch (ex) {
            logError(ex);
            throw new Error("Failed to remove credit card");
        }
    }
}
